import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatMoney, formatDate } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';
import TransactionModal from '../Transactions/TransactionModal';

// Agrupa os pendentes por urgência — o olho vai direto no que pega fogo
const GROUPS = [
  { id: 'late',  label: '🔴 Atrasadas',        color: 'var(--red)' },
  { id: 'today', label: '🟠 Vencem hoje',       color: 'var(--gold)' },
  { id: 'week',  label: '🟡 Próximos 7 dias',   color: 'var(--gold)' },
  { id: 'month', label: '🔵 Até o fim do mês',  color: 'var(--blue)' },
  { id: 'later', label: '⚪ Mais pra frente',    color: 'var(--muted)' },
];

export default function Forecasts() {
  const { state, dispatch, totalBalance } = useApp();
  const [typeFilter, setTypeFilter] = useState('all'); // all | income | expense
  const [editingTx, setEditingTx] = useState(null);

  const cats = useMemo(() => Object.fromEntries((state.categories || []).map(c => [c.id, c])), [state.categories]);

  const isoDay = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const hoje = isoDay(0);
  const seteDias = isoDay(7);
  const fimDoMes = `${hoje.slice(0, 7)}-31`;

  const pending = useMemo(() =>
    (state.transactions || []).filter(t => t.status === 'pending' && t.date).sort((a, b) => a.date.localeCompare(b.date)),
    [state.transactions]);

  const filtered = typeFilter === 'all' ? pending : pending.filter(t => t.type === typeFilter);

  const groupOf = (t) => {
    if (t.date < hoje) return 'late';
    if (t.date === hoje) return 'today';
    if (t.date <= seteDias) return 'week';
    if (t.date <= fimDoMes) return 'month';
    return 'later';
  };
  const grouped = useMemo(() => {
    const m = { late: [], today: [], week: [], month: [], later: [] };
    filtered.forEach(t => m[groupOf(t)].push(t));
    return m;
  }, [filtered]); // eslint-disable-line

  // Totais em R$ (pendentes em US$ entram convertidos pelo câmbio do dia)
  const emBRL = (t) => toBRL(t.value, t.currency, state.usdRate);
  const toPay = pending.filter(t => t.type === 'expense').reduce((s, t) => s + emBRL(t), 0);
  const toReceive = pending.filter(t => t.type === 'income').reduce((s, t) => s + emBRL(t), 0);
  // Saldo projetado: se tudo que está pendente se confirmar, onde você termina?
  const projected = totalBalance + toReceive - toPay;

  const toggleStatus = (tx) => dispatch({ type: 'TOGGLE_TX_STATUS', payload: tx.id });

  return (
    <div className="page-enter">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Previsões</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>O que ainda está pendente, do mais urgente pro mais distante</div>
      </div>

      {/* Saldo projetado — a pergunta que a tela responde: "vai dar?" */}
      <div className="card" style={{ marginBottom: 14, background: projected < 0 ? 'rgba(226,75,74,0.06)' : undefined, borderColor: projected < 0 ? 'rgba(226,75,74,0.35)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="metric-label">Saldo projetado (se tudo se confirmar)</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: projected >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 4 }}>{formatCurrency(projected)}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              saldo atual {formatCurrency(totalBalance)} + a receber − a pagar
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 700 }}>+{formatCurrency(toReceive)} <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>a receber</span></div>
            <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 700, marginTop: 4 }}>−{formatCurrency(toPay)} <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>a pagar</span></div>
          </div>
        </div>
        {projected < 0 && (
          <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600, marginTop: 8 }}>
            ⚠️ Se tudo vencer como está, o saldo fica negativo — vale renegociar prazos ou reforçar a receita.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { id: 'all', label: 'Todos' },
          { id: 'expense', label: 'Despesas' },
          { id: 'income', label: 'Receitas' },
        ].map(f => (
          <button
            key={f.id}
            className={`btn btn-sm ${typeFilter === f.id ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTypeFilter(f.id)}
            style={{ fontSize: 12, padding: '5px 12px', flex: 1, justifyContent: 'center' }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <i className="fas fa-calendar-check"></i>
            <div>Nada pendente por aqui — tudo em dia!</div>
          </div>
        </div>
      ) : GROUPS.map(g => {
        const txs = grouped[g.id];
        if (!txs.length) return null;
        const gPay = txs.filter(t => t.type === 'expense').reduce((s, t) => s + emBRL(t), 0);
        const gRec = txs.filter(t => t.type === 'income').reduce((s, t) => s + emBRL(t), 0);
        return (
          <div className="card" key={g.id} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: g.color }}>{g.label} <span style={{ color: 'var(--muted)', fontWeight: 500 }}>({txs.length})</span></div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                {gPay > 0 && <span style={{ color: 'var(--red)' }}>−{formatCurrency(gPay)}</span>}
                {gPay > 0 && gRec > 0 && <span style={{ color: 'var(--muted)' }}> · </span>}
                {gRec > 0 && <span style={{ color: 'var(--green)' }}>+{formatCurrency(gRec)}</span>}
              </div>
            </div>
            {txs.map(tx => {
              const cat = cats[tx.categoryId];
              const overdue = g.id === 'late';
              return (
                <div className="tx-item" key={tx.id} style={{ gap: 10 }}>
                  <div className="tx-icon">
                    <i className={cat?.icon || 'fas fa-circle'}></i>
                  </div>
                  <div className="tx-info" style={{ cursor: 'pointer' }} onClick={() => setEditingTx(tx)}>
                    <div className="tx-desc">{tx.description}</div>
                    <div className="tx-cat">{cat?.name}{tx.subcategory ? ` › ${tx.subcategory}` : ''}</div>
                  </div>
                  <div className="tx-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                    <div className={`tx-amount ${tx.type === 'income' ? 'text-green' : 'text-red'}`}>
                      {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                    </div>
                    <div className="tx-date">{formatDate(tx.date + 'T12:00:00')}</div>
                    <button
                      onClick={() => toggleStatus(tx)}
                      style={{
                        background: overdue ? '#e24b4a' : tx.type === 'income' ? '#1d9e75' : 'var(--gold)',
                        border: 'none', color: '#fff', borderRadius: 99, padding: '4px 12px',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                      }}
                    >
                      <i className="fas fa-circle-check"></i> {overdue ? 'Atrasado — Pagar' : tx.type === 'income' ? 'Marcar recebido' : 'Marcar pago'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {editingTx && <TransactionModal tx={editingTx} onClose={() => setEditingTx(null)} />}
    </div>
  );
}
